import React from 'react';
import PropTypes from 'prop-types';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { cloneObject } from '../../../common/utils';
import { allowedMessageProps } from '../../lists/SystemMessages';
import { UnorderedList } from '../../lists/UnorderedList';
import Loader from '../Loader';
import DropdownArea from '../DropdownArea';

const isGoogleDefined = typeof (google) !== 'undefined';

class InputFieldAddress extends React.Component {
  constructor(props) {
    super(props);

    const {
      inputAttributes: {
        value: {
          address = '',
          location: {
            latitude = 0,
            longitude = 0
          }
        }
      }
    } = props;

    this.state = {
      address,
      location: {
        latitude,
        longitude
      }
    };


    this.sessionToken = isGoogleDefined ? new google.maps.places.AutocompleteSessionToken() : '';
    this.setAddress = this.setAddress.bind(this);
    this.setLocation = this.setLocation.bind(this);
  }

  componentDidMount() {
    const {
      state: {
        address,
        location: {
          latitude,
          longitude
        }
      },
      setLocation
    } = this;

    if (address && (!latitude || !longitude)) {
      setLocation();
    }
  }

  setAddress(address, resolve) {
    const {
      props: {
        customChangeHandler,
        inputAttributes: {
          name: fieldName
        },
        setFieldValue
      },
      state: {
        location
      },
      setLocation
    } = this;

    const value = {
      address,
      location
    };

    if (customChangeHandler) {
      customChangeHandler({ value });
    }

    else {
      setFieldValue(fieldName, value);
    }

    this.setState({ address }, () => {
      setLocation(resolve);
    });
  }

  async setLocation(resolve) {
    const {
      props: {
        customChangeHandler,
        onGeocodeProcess,
        inputAttributes: {
          name: fieldName
        },
        setFieldValue
      },
      state: {
        address,
        location
      }
    } = this;

    const value = {
      address,
      location
    };

    if (customChangeHandler) {
      customChangeHandler({ value });
    }

    else {
      setFieldValue(fieldName, value);
    }

    const geocodes = await geocodeByAddress(address);

    try {
      const {
        lat: latitude,
        lng: longitude
      } = await getLatLng(geocodes[0]);

      value.location = {
        latitude,
        longitude
      };

      if (customChangeHandler) {
        customChangeHandler({ value });
      }

      else {
        setFieldValue(fieldName, value);
      }
    }

    catch (error) {
      console.error(`Address input - location error: ${error}`);
    }

    if (onGeocodeProcess) {
      onGeocodeProcess(value);
    }

    if (resolve) {
      resolve();
    }
  }

  render() {
    const {
      props: {
        inputAttributes,
        suggestionListTitle,
        message,
        label,
        description: inputDescription,
        onAddressChange,
        onAddressSelect,
        placeSuggestionTypes,
        placeSuggestionMeterRadius = 5000,

        // TODO: REPLACE WITH THIS ENDPOINT:
        // https://www.wannasport.com/geolocation - Claudio: I expect location, types and radius to be passed as props
        placeSearchLocation = isGoogleDefined ? new google.maps.LatLng(55.0, 11.0) : ''
      },
      state: {
        address
      },
      sessionToken,
      setAddress
    } = this;

    return (
      <PlacesAutocomplete
        value={address}
        onChange={!onAddressChange ? setAddress : inputValue => {
          onAddressChange(() => new Promise((resolve => {
            setAddress(inputValue, resolve);
          })));
        }}
        onSelect={!onAddressSelect ? setAddress : inputValue => {
          onAddressSelect(() => new Promise((resolve => {
            setAddress(inputValue, resolve);
          })));
        }}
        searchOptions={{
          sessionToken,
          radius: placeSuggestionMeterRadius,
          types: placeSuggestionTypes,
          location: placeSearchLocation
        }}

        /*
        // Keep this, its handy when something needs to be checked in the inspector:
        // (blocks the suggestions from resetting when input is blurred)
        ref={c => {
          if (!c) return;
          c.handleInputOnBlur = () => {};
        }}
        */
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
          const clonedInputAttributes = cloneObject(inputAttributes, {}, [
            'onChange', // removing onchange provided by Formik (so PlacesAutocomplete can handle it)
            'value' // removing value provided by Formik (so it can be controlled by the component state)*

            /*
            NOTE: keep the value controlled by the state of this component! If you update the formik state everytime
            the location object changes when processing geocode, it sets back the input value to the one before the
            update -> it would become a struggle to type in the field.

            This comment applies if the value shape of the address input is:
            {
              address: '',
              location: {
                latitude: 0,
                longitude: 0
              }
            }
            */
          ]);

          const finalInputAttributes = cloneObject(clonedInputAttributes, {
            ...getInputProps({ 'aria-invalid': !!message }),
            title: !label ? inputDescription : null
          }, ['wrapperClasses']);

          const dropdownClasses = ['input-address-suggestions-dropdown'];

          if (loading) {
            dropdownClasses.push('is-loading');
          }

          return (
            <React.Fragment>
              <input {...finalInputAttributes} />
              <DropdownArea
                isOpen={loading || suggestions.length > 0}
                maxHeight={`${suggestions.length * 6}em`}
                classNames={dropdownClasses}
              >
                <Loader hasLoaded={!loading}>
                  <UnorderedList title={suggestionListTitle}>
                    {suggestions.map(suggestion => {
                      const {
                        active,
                        description
                      } = suggestion;

                      const className = `input-suggestion-item${active ? '--active' : ''}`;

                      return (
                        <li {...getSuggestionItemProps(suggestion, { className })}>
                          <span>{description}</span>
                        </li>
                      );
                    })}
                  </UnorderedList>
                </Loader>
              </DropdownArea>
            </React.Fragment>
          );
        }}
      </PlacesAutocomplete>
    );
  }
}

InputFieldAddress.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func,
  onAddressSelect: PropTypes.func,
  onGeocodeProcess: PropTypes.func,
  inputAttributes: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.shape({
      address: PropTypes.string.isRequired,
      location: PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  label: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.bool
  ]),
  description: PropTypes.string.isRequired,
  message: PropTypes.shape(allowedMessageProps),
  customChangeHandler: PropTypes.func,
  placeSuggestionMeterRadius: PropTypes.number,
  placeSearchLocation: PropTypes.object, // TODO: improve this prop check
  suggestionListTitle: PropTypes.string.isRequired,
  placeSuggestionTypes: PropTypes.arrayOf(
    PropTypes.oneOf(['(cities)'])
  )
};

export default InputFieldAddress;